import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import * as XLSX from "xlsx";
import { RefreshCw, Download, Trash2, FileText, Search, X, Wrench, ChevronRight } from "lucide-react";
import { MaintenanceRecord, money } from "./types";
import { formatDate } from "@/lib/dates";

interface Props {
  systems: string[];
  workTypes: string[];
  refreshKey: number;
  scopedVehicleNumbers?: string[] | null;
  canEdit?: boolean;
}

const RANGE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 3 months" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last 12 months" },
  { value: "ytd", label: "This year" },
];

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees maintenance records only for their linked vehicle(s), and can only
// delete a record with edit rights on Fleet.
const MaintenanceReport: React.FC<Props> = ({ systems, workTypes, refreshKey, scopedVehicleNumbers = null, canEdit = true }) => {
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [vehicleSearch, setVehicleSearch] = useState("");
  const [systemFilter, setSystemFilter] = useState("all");
  const [workTypeFilter, setWorkTypeFilter] = useState("all");
  const [range, setRange] = useState("30");
  const [textSearch, setTextSearch] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(true);
  const [itemsOpen, setItemsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    let query = supabase
      .from("maintenance_records")
      .select("*")
      .order("service_date", { ascending: false })
      .limit(5000);
    if (scopedVehicleNumbers) {
      query = query.in("vehicle_number", scopedVehicleNumbers.length > 0 ? scopedVehicleNumbers : ["__none__"]);
    }
    const { data, error } = await query;
    setLoading(false);
    if (error) {
      toast({ title: "Load failed", description: error.message, variant: "destructive" });
      return;
    }
    setRecords((data || []) as MaintenanceRecord[]);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, scopedVehicleNumbers]);

  const rangeStartIso = useMemo(() => {
    if (range === "all") return null;
    const now = new Date();
    if (range === "ytd") return new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
    const days = Number(range);
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [range]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (vehicleSearch.trim() && !String(r.vehicle_number).toLowerCase().includes(vehicleSearch.trim().toLowerCase())) return false;
      if (systemFilter !== "all" && r.system_name !== systemFilter) return false;
      if (workTypeFilter !== "all" && r.work_type_name !== workTypeFilter) return false;
      if (rangeStartIso && (r.service_date || "") < rangeStartIso) return false;
      if (textSearch.trim()) {
        const t = textSearch.trim().toLowerCase();
        const hay = `${r.notes || ""} ${r.supplier || ""} ${r.system_name || ""} ${r.work_type_name || ""}`.toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
  }, [records, vehicleSearch, systemFilter, workTypeFilter, rangeStartIso, textSearch]);

  const isGstRow = (r: MaintenanceRecord) =>
    r.is_gst === true || (r.system_name || "").toUpperCase() === "GST" || (r.work_type_name || "").toUpperCase() === "GST";

  const totals = useMemo(() => {
    let parts = 0, labour = 0, gst = 0;
    for (const r of filtered) {
      const c = Number(r.cost) || 0;
      if (isGstRow(r)) gst += c;
      else if (r.is_labour) labour += c;
      else parts += c;
    }
    return { parts, labour, gst, total: parts + labour + gst, count: filtered.length };
  }, [filtered]);

  // Group records by invoice so each invoice shows System / Work Type / Parts / Labour / GST / Total.
  const invoiceGroups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; vehicle: string; date: string; supplier: string; systems: Set<string>; workTypes: Set<string>; parts: number; labour: number; gst: number }>();
    for (const r of filtered) {
      const key = r.invoice_id || `${r.vehicle_number}|${r.service_date}|${r.supplier || ""}`;
      const label = r.invoice_id ? `Invoice ${String(r.invoice_id).slice(0, 8)}` : `${r.vehicle_number} · ${formatDate(r.service_date)}`;
      const g = map.get(key) || { key, label, vehicle: r.vehicle_number, date: r.service_date, supplier: r.supplier || "", systems: new Set<string>(), workTypes: new Set<string>(), parts: 0, labour: 0, gst: 0 };
      const c = Number(r.cost) || 0;
      if (isGstRow(r)) g.gst += c; else if (r.is_labour) g.labour += c; else g.parts += c;
      if (!isGstRow(r)) {
        if (r.system_name) g.systems.add(r.system_name);
        if (r.work_type_name) g.workTypes.add(r.work_type_name);
      }
      map.set(key, g);
    }
    return Array.from(map.values()).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [filtered]);


  const clearFilters = () => {
    setVehicleSearch(""); setSystemFilter("all"); setWorkTypeFilter("all"); setRange("30"); setTextSearch("");
  };

  const exportXlsx = () => {
    if (filtered.length === 0) {
      toast({ title: "Nothing to export", variant: "destructive" });
      return;
    }
    const rows = filtered.map((r) => ({
      "Fleet #": r.vehicle_number,
      Date: formatDate(r.service_date),
      System: r.system_name || "",
      "Work Type": r.work_type_name || "",
      "Parts/Labour/GST": isGstRow(r) ? "GST" : (r.is_labour ? "Labour" : "Parts"),

      Cost: r.cost ?? "",
      Odometer: r.odometer ?? "",
      Supplier: r.supplier || "",
      Notes: r.notes || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Maintenance");
    XLSX.writeFile(wb, `maintenance_report_${Date.now()}.xlsx`);
  };

  const viewInvoice = async (invoiceId: string | null) => {
    if (!invoiceId) return;
    const { data, error } = await supabase.from("maintenance_invoices").select("file_path").eq("id", invoiceId).single();
    if (error || !data?.file_path) {
      toast({ title: "Invoice not found", variant: "destructive" });
      return;
    }
    const { data: pub } = supabase.storage.from("vehicle-invoices").getPublicUrl(data.file_path);
    if (pub?.publicUrl) window.open(pub.publicUrl, "_blank");
  };

  const remove = async (r: MaintenanceRecord) => {
    if (!canEdit) return;
    if (!window.confirm(`Delete this maintenance record for ${r.vehicle_number}?`)) return;
    const { error } = await supabase.from("maintenance_records").delete().eq("id", r.id);
    if (error) {
      toast({ title: "Not deleted", description: error.message, variant: "destructive" });
      return;
    }
    await load();
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-yellow-500" />
          Maintenance Records & Reporting
          <Badge variant="secondary">{filtered.length}</Badge>
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportXlsx}><Download className="w-4 h-4 mr-2" />Export</Button>
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 rounded-xl border border-border bg-background/40 p-4">
          <div className="space-y-1">
            <Label className="text-xs">Vehicle (Fleet #)</Label>
            <Input value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)} placeholder="e.g. 422" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Vehicle System</Label>
            <Select value={systemFilter} onValueChange={setSystemFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[260px]">
                <SelectItem value="all">All systems</SelectItem>
                {systems.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Work Type</Label>
            <Select value={workTypeFilter} onValueChange={setWorkTypeFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[260px]">
                <SelectItem value="all">All work types</SelectItem>
                {workTypes.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Period</Label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Search notes/supplier</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8" value={textSearch} onChange={(e) => setTextSearch(e.target.value)} placeholder="keyword" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">Records: {totals.count}</Badge>
            <Badge variant="outline" className="border-blue-700/50 text-blue-300">Parts: {money(totals.parts)}</Badge>
            <Badge variant="outline" className="border-orange-700/50 text-orange-300">Labour: {money(totals.labour)}</Badge>
            <Badge variant="outline" className="border-emerald-700/50 text-emerald-300">GST: {money(totals.gst)}</Badge>
            <Badge variant="outline" className="border-green-700/50 text-green-300">Total (incl GST): {money(totals.total)}</Badge>
          </div>

          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
            <X className="w-4 h-4 mr-1" /> Clear filters
          </Button>
        </div>
        {/* Per-invoice breakdown: Parts / Labour / GST / Invoice Total */}
        <div className="rounded-lg border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setInvoiceOpen((o) => !o)}
            className="w-full flex items-center gap-2 px-4 py-3 bg-muted/30 hover:bg-muted/50 text-left"
          >
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${invoiceOpen ? "rotate-90" : ""}`} />
            <span className="font-medium text-sm">Invoice / Service Summary</span>
            <Badge variant="secondary" className="ml-1">{invoiceGroups.length}</Badge>
          </button>
          {invoiceOpen && (
            invoiceGroups.length > 0 ? (
              <div className="overflow-auto max-h-[320px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>Invoice / Service</TableHead>
                      <TableHead className="w-20">Fleet #</TableHead>
                      <TableHead className="w-28">Date</TableHead>
                      <TableHead>System</TableHead>
                      <TableHead>Work Type</TableHead>
                      <TableHead className="w-28 text-right">Parts</TableHead>
                      <TableHead className="w-28 text-right">Labour</TableHead>
                      <TableHead className="w-28 text-right">GST</TableHead>
                      <TableHead className="w-32 text-right">Invoice Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceGroups.map((g) => (
                      <TableRow key={g.key}>
                        <TableCell className="text-xs">{g.label}{g.supplier ? ` · ${g.supplier}` : ""}</TableCell>
                        <TableCell className="font-medium">{g.vehicle}</TableCell>
                        <TableCell>{formatDate(g.date)}</TableCell>
                        <TableCell className="text-xs">{g.systems.size ? Array.from(g.systems).join(", ") : "-"}</TableCell>
                        <TableCell className="text-xs">{g.workTypes.size ? Array.from(g.workTypes).join(", ") : "-"}</TableCell>
                        <TableCell className="text-right text-blue-300">{money(g.parts)}</TableCell>
                        <TableCell className="text-right text-orange-300">{money(g.labour)}</TableCell>
                        <TableCell className="text-right text-emerald-300">{money(g.gst)}</TableCell>
                        <TableCell className="text-right font-semibold text-green-400">{money(g.parts + g.labour + g.gst)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">No invoices match your filters.</div>
            )
          )}
        </div>

        {/* Itemised records (collapsed by default, expand to view) */}
        <div className="rounded-lg border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setItemsOpen((o) => !o)}
            className="w-full flex items-center gap-2 px-4 py-3 bg-muted/30 hover:bg-muted/50 text-left"
          >
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${itemsOpen ? "rotate-90" : ""}`} />
            <span className="font-medium text-sm">Itemised Records</span>
            <Badge variant="secondary" className="ml-1">{filtered.length}</Badge>
          </button>
          {itemsOpen && (
            <div className="overflow-auto max-h-[560px] border-t border-border">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-20">Fleet #</TableHead>
                    <TableHead className="w-28">Date</TableHead>
                    <TableHead>System</TableHead>
                    <TableHead>Work Type</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-28 text-right">Cost</TableHead>
                    <TableHead className="w-24">Supplier</TableHead>
                    <TableHead className="w-24 text-center">Invoice</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.vehicle_number}</TableCell>
                      <TableCell>{formatDate(r.service_date)}</TableCell>
                      <TableCell>{r.system_name || "-"}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          {r.work_type_name || "-"}
                          {isGstRow(r) && <Badge variant="outline" className="text-[10px] border-emerald-700/50 text-emerald-300">GST</Badge>}
                          {!isGstRow(r) && r.is_labour && <Badge variant="outline" className="text-[10px] border-orange-700/50 text-orange-300">Labour</Badge>}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate" title={r.notes || ""}>{r.notes || "-"}</TableCell>
                      <TableCell className="text-right">{money(r.cost)}</TableCell>
                      <TableCell className="text-xs">{r.supplier || "-"}</TableCell>
                      <TableCell className="text-center">
                        {r.invoice_id ? (
                          <Button variant="ghost" size="sm" onClick={() => viewInvoice(r.invoice_id)} title="View invoice">
                            <FileText className="w-4 h-4 text-blue-400" />
                          </Button>
                        ) : <span className="text-muted-foreground text-xs">-</span>}
                      </TableCell>
                      <TableCell>
                        {canEdit && (
                          <Button variant="ghost" size="sm" onClick={() => remove(r)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        {loading ? "Loading..." : "No maintenance records match your filters."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

      </CardContent>
    </Card>
  );
};

export default MaintenanceReport;
